import { useEffect, useRef, useState } from 'react';
import {
  BookOpen,
  Check,
  Home,
  MessagesSquare,
  Plus,
  Settings,
  SlidersHorizontal,
  UserRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Me } from '../api/auth';
import { familyConversationsApi } from '../api/conversations';
import { familyWhatsNewApi } from '../api/whatsNew';
import { useLiveMessages } from '../hooks/useLiveMessages';
import { HeaderButton } from './HeaderButton';

/**
 * Who you are, and everywhere the icons do not go — TROOP-120, shared by both headers in TROOP-122.
 *
 * **One control on every page.** It began inside `AppHeader`, which only the main pages use; the
 * detail pages carried `AppBar` instead, whose right-hand side was a Bell and an Ellipsis that had
 * never been wired to anything. Two different headers, one of them with two dead controls, is the
 * inconsistency this closes: the same account control now sits in the same place whichever page
 * you are on, and the dead pair is gone rather than implemented, because neither had a feature
 * behind it to reach.
 *
 * **The trigger is an icon, not an initials avatar**, deliberately: `ParticipantChip` gives a
 * circular avatar the meaning *adult* and a rounded square the meaning *youth*, carried by shape
 * because it must never be carried by colour. A circular avatar of the signed-in person would be a
 * fourth thing wearing that vocabulary, and would quietly call a youth Patrol Leader an adult.
 *
 * **It carries the family unread badge when the family door is behind it.** Collapsing a
 * destination is allowed to cost a tap; it is not allowed to cost the signal that somebody has
 * been told something, which is the whole reason TROOP-105 put that door in the chrome. The count
 * is fetched here and only when this component is the thing that would show it — a guardian
 * holding no membership reaches their family through `AppHeader`'s icons instead, and that badge
 * is fetched there, so the two never both ask.
 *
 * A `role="menu"` dropdown and deliberately not an `Overlay` — the distinction the design system
 * draws for the unit switcher this grew out of. Do not merge them.
 */
export function AccountMenu({
  me,
  currentUnitId,
  onNavigate,
}: {
  me: Me;
  currentUnitId?: string;
  onNavigate: (path: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [unreadFamily, setUnreadFamily] = useState(0);
  const [newFamily, setNewFamily] = useState(0);
  const menu = useRef<HTMLDivElement>(null);
  const live = useLiveMessages();

  const current = me.memberships.find((m) => m.unitId === currentUnitId) ?? me.memberships[0];
  const multiUnit = me.memberships.length > 1;

  // The family door is behind this menu only for somebody who also holds a membership. For
  // everybody else it is one of the header's own icons and is badged there.
  const badgesFamily = Boolean(me.isGuardian && current);

  useEffect(() => {
    if (!badgesFamily) {
      setUnreadFamily(0);
      return;
    }

    let alive = true;

    void familyConversationsApi
      .unreadTotal()
      .then((summary) => {
        if (alive) setUnreadFamily(summary.total);
      })
      // Silent: chrome that cannot count is chrome without a badge, not an error banner over
      // somebody's calendar.
      .catch(() => undefined);

    return () => {
      alive = false;
    };
  }, [badgesFamily, live.tick]);

  // What is new behind the family door, for the same person (TROOP-268). Their home icon opens the
  // unit they lead, so the family door's count has nowhere else to live -- and collapsing a
  // destination must not collapse its badge. Keyed on the door rather than the live tick, because
  // nothing nudges for a new announcement or event.
  useEffect(() => {
    if (!badgesFamily) {
      setNewFamily(0);
      return;
    }

    let alive = true;

    void familyWhatsNewApi
      .count()
      .then((summary) => {
        if (alive) setNewFamily(summary.total);
      })
      .catch(() => undefined);

    return () => {
      alive = false;
    };
  }, [badgesFamily]);

  // Escape closes, and a click outside closes. Both are what a role="menu" owes a keyboard user
  // and a thumb; neither is Overlay's business, which is why this is not one.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    const onPointerDown = (e: PointerEvent) => {
      if (menu.current && !menu.current.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const go = (path: string) => {
    setOpen(false);
    onNavigate(path);
  };

  const showFamily = Boolean(me.isGuardian && current);

  return (
    <div style={{ position: 'relative' }} ref={menu}>
      <HeaderButton
        label={open ? 'Close menu' : `${me.displayName} and settings`}
        onClick={() => setOpen((o) => !o)}
        // The dot says something is waiting behind the family door, messages or new items alike;
        // the menu rows below say which.
        badge={unreadFamily + newFamily}
        badgeNoun="new"
        expanded={open}
      >
        <UserRound size={18} aria-hidden="true" />
      </HeaderButton>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 4px)',
            minWidth: '15rem',
            maxWidth: 'calc(100vw - var(--space-5) * 2)',
            background: 'var(--color-surface-raised)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--elevation-overlay)',
            zIndex: 20,
            overflow: 'hidden',
            paddingBlock: 'var(--space-1)',
          }}
        >
          {/* Your units, and which one you are looking at. A single-unit member sees no group at
              all rather than a list of one -- the rule the switcher already followed, kept. */}
          {multiUnit && (
            <MenuGroup label="Your units" first>
              {me.memberships.map((membership) => (
                <MenuItem
                  key={membership.unitId}
                  label={membership.unitName}
                  detail={membership.positionTitle}
                  selected={membership.unitId === current?.unitId}
                  onClick={() => {
                    setOpen(false);
                    // Switching reloads the shell; two units' content is never merged.
                    window.location.href = `/units/${membership.unitId}`;
                  }}
                />
              ))}
            </MenuGroup>
          )}

          {showFamily && (
            <MenuGroup label="Your family" first={!multiUnit}>
              <MenuItem
                icon={Home}
                label="Your family"
                detail="Your children, and their accounts"
                badge={newFamily}
                badgeNoun="new"
                onClick={() => go('/family')}
              />
              <MenuItem
                icon={MessagesSquare}
                label="Family conversations"
                detail="Across every unit your children are in"
                badge={unreadFamily}
                onClick={() => go('/family/conversations')}
              />
              {/* TROOP-256. The pages of every unit their children are in -- not the unit this
                  person leads, whose page is in the group below. */}
              <MenuItem
                icon={BookOpen}
                label="Info from their units"
                detail="Where each meets, dues, uniform, who to ask"
                onClick={() => go('/family/info')}
              />
            </MenuGroup>
          )}

          <MenuGroup
            label={current ? current.unitName : 'You'}
            first={!multiUnit && !showFamily}
          >
            {/* TROOP-256. Behind the menu rather than a fourth header icon: the header is three
                icons and an account menu. A parent with no membership reaches the same page
                through /family. */}
            {current ? (
              <MenuItem
                icon={BookOpen}
                label="Info"
                detail="Where the unit meets, dues, uniform, who to ask"
                onClick={() => go(`/units/${current.unitId}/info`)}
              />
            ) : (
              me.isGuardian && (
                <MenuItem
                  icon={BookOpen}
                  label="Info from your children's units"
                  detail="Where each meets, dues, uniform, who to ask"
                  onClick={() => go('/family/info')}
                />
              )
            )}

            {current && (
              <MenuItem
                icon={SlidersHorizontal}
                label="Manage"
                detail="Roster, groups, kinds of event, channels, what to bring"
                onClick={() => go(`/units/${current.unitId}/manage`)}
              />
            )}

            <MenuItem
              icon={Settings}
              label="Your settings"
              detail="Calendar feed, driving, your own lists"
              onClick={() => go('/settings')}
            />

            {me.isSystemAdmin && (
              <MenuItem
                icon={Plus}
                label="Invites"
                detail="Found a new unit"
                onClick={() => go('/admin/invites')}
              />
            )}
          </MenuGroup>
        </div>
      )}
    </div>
  );
}

/**
 * A labelled run of menu items, with a hairline above it when it is not the first.
 *
 * `first` is passed rather than derived from a `first:` variant: the border is an inline style and
 * an inline style always wins, so the class could never remove it. It drew a stray hairline across
 * the top group, hard against the card's own edge.
 */
function MenuGroup({
  label,
  first,
  children,
}: {
  label: string;
  first: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        paddingBlock: 'var(--space-1)',
        borderTop: first ? undefined : '1px solid var(--color-border-subtle)',
      }}
    >
      <p
        className="t-eyebrow"
        style={{
          color: 'var(--color-text-muted)',
          margin: 0,
          padding: 'var(--space-2) var(--space-4) var(--space-1)',
        }}
      >
        {label}
      </p>
      {children}
    </div>
  );
}

/**
 * One destination in the account menu.
 *
 * The detail line is not decoration: a menu of six bare nouns is a menu somebody has to open twice
 * to learn. It says what is behind the item, which is what a springboard row does one level down.
 */
function MenuItem({
  icon: Icon,
  label,
  detail,
  selected,
  badge,
  badgeNoun = 'unread',
  onClick,
}: {
  icon?: LucideIcon;
  label: string;
  detail?: string;
  selected?: boolean;
  badge?: number;
  /** What the number counts, for a screen reader -- messages are unread, items are new. */
  badgeNoun?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center justify-between gap-3 text-left"
      style={{
        minHeight: 'var(--tap-min)',
        padding: 'var(--space-2) var(--space-4)',
        background: selected ? 'var(--color-surface-sunken)' : 'transparent',
        border: 'none',
        color: 'var(--color-text-primary)',
      }}
    >
      <span className="flex items-center gap-3 min-w-0">
        {Icon && (
          <Icon
            size={16}
            aria-hidden="true"
            style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}
          />
        )}
        <span className="min-w-0">
          <span className="t-body-strong truncate" style={{ display: 'block' }}>
            {label}
          </span>
          {detail && (
            <span
              className="t-caption truncate"
              style={{ color: 'var(--color-text-secondary)', display: 'block' }}
            >
              {detail}
            </span>
          )}
        </span>
      </span>

      {selected && (
        <Check size={16} aria-hidden="true" style={{ color: 'var(--color-text-secondary)' }} />
      )}

      {badge !== undefined && badge > 0 && (
        <span
          className="t-caption tabular"
          aria-label={`${badge} ${badgeNoun}`}
          style={{ color: 'var(--color-accent-announcement-text)', flexShrink: 0 }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}
